import React, { useState, useEffect, useRef } from 'react';
import { Skeleton } from './skeleton';

interface OptimizedImageProps {
  src: string | null;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  width?: number;
  height?: number;
  lazy?: boolean;
  priority?: boolean;
}

export const OptimizedImage = React.memo<OptimizedImageProps>(({
  src,
  alt,
  className = '',
  fallbackSrc = '/placeholder-image.png',
  width,
  height,
  lazy = true,
  priority = false,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || !imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before the image comes into view
        threshold: 0.1,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [lazy]);

  // Handle image loading
  useEffect(() => {
    if (!isInView) return;

    const img = new Image();
    
    const handleLoad = () => {
      setIsLoading(false);
      setHasError(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      // Only set fallback if we're not already trying to load the fallback
      if (imageSrc && imageSrc !== fallbackSrc) {
        setImageSrc(fallbackSrc);
      }
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    if (imageSrc && imageSrc.trim() !== '') {
      img.src = imageSrc;
    } else {
      handleError();
    }

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc, isInView, fallbackSrc]);

  // Update image source when src prop changes
  useEffect(() => {
    if (src !== imageSrc) {
      setImageSrc(src);
      setIsLoading(true);
      setHasError(false);
    }
  }, [src]); // Remove imageSrc from dependency array to prevent infinite loop

  // Handle null or empty src
  useEffect(() => {
    if (!src || src.trim() === '') {
      setImageSrc(null);
      setIsLoading(false);
      setHasError(true);
    }
  }, [src]);

  if (!isInView) {
    return (
      <div 
        ref={imgRef}
        className={`${className} bg-muted animate-pulse`}
        style={{ width, height }}
      >
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${className} bg-muted`} style={{ width, height }}>
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (hasError || !imageSrc) {
    return (
      <div 
        className={`${className} bg-muted flex items-center justify-center text-muted-foreground`}
        style={{ width, height }}
      >
        <span className="text-xs">No Image</span>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
});

OptimizedImage.displayName = 'OptimizedImage'; 